import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  keyframes,
} from '@mui/material';
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  CallEnd,
  ScreenShare,
  StopScreenShare,
  FullscreenExit,
  Fullscreen,
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import Peer from 'simple-peer';
import { axelionColors } from '../../theme/axelionTheme';
import api from '../../services/api';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Derive Socket.IO server URL: strip /api suffix from API URL if present
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api\/?$/, '');

const VideoCallPage = () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  // State
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [peerConnected, setPeerConnected] = useState(false);
  const [remoteName, setRemoteName] = useState('');
  const [remoteRole, setRemoteRole] = useState('');

  // Media controls
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Call state
  const [callDuration, setCallDuration] = useState(0);
  const [callStartTime, setCallStartTime] = useState(null);

  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const socketRef = useRef(null);
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const pendingSignalsRef = useRef([]);
  const callStartedRef = useRef(false);

  // Load consultation details
  useEffect(() => {
    const loadConsultation = async () => {
      try {
        const response = await api.get(`/video/consultation/${consultationId}`);
        setConsultation(response.data);
      } catch (err) {
        setError('Failed to load consultation details');
        console.error('Load consultation error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadConsultation();
  }, [consultationId]);

  // Start call timer
  useEffect(() => {
    if (peerConnected && !callStartTime) {
      setCallStartTime(Date.now());
    }
  }, [peerConnected, callStartTime]);

  useEffect(() => {
    if (callStartTime) {
      timerRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTime) / 1000));
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [callStartTime]);

  // Format call duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Create WebRTC peer connection
  const createPeer = useCallback((targetSocketId, stream, initiator) => {
    // Clear stale pending signals from any previous peer
    pendingSignalsRef.current = [];

    if (peerRef.current) {
      peerRef.current.destroy();
    }

    const peer = new Peer({
      initiator,
      trickle: true,
      stream,
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' },
          // Free TURN servers for NAT traversal
          {
            urls: 'turn:openrelay.metered.ca:80',
            username: 'openrelayproject',
            credential: 'openrelayproject',
          },
          {
            urls: 'turn:openrelay.metered.ca:443',
            username: 'openrelayproject',
            credential: 'openrelayproject',
          },
          {
            urls: 'turn:openrelay.metered.ca:443?transport=tcp',
            username: 'openrelayproject',
            credential: 'openrelayproject',
          },
        ],
        iceCandidatePoolSize: 10,
      },
    });

    peer.on('signal', (signal) => {
      socketRef.current?.emit('signal', { to: targetSocketId, signal });
    });

    peer.on('stream', (remoteStream) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
      }
      setPeerConnected(true);
    });

    peer.on('close', () => {
      setPeerConnected(false);
    });

    peer.on('error', (err) => {
      console.error('Peer error:', err);
      // Don't show error for non-critical peer issues during negotiation
      if (err.code === 'ERR_DATA_CHANNEL' || err.code === 'ERR_CONNECTION_FAILURE') {
        setError('Не удалось установить соединение. Проверьте интернет и попробуйте снова.');
      }
    });

    peerRef.current = peer;

    // Flush any pending signals that arrived before peer was created
    if (pendingSignalsRef.current.length > 0) {
      pendingSignalsRef.current.forEach((sig) => {
        try {
          peer.signal(sig);
        } catch (e) {
          console.error('Error applying pending signal:', e);
        }
      });
      pendingSignalsRef.current = [];
    }
  }, []);

  // End call
  const handleEndCall = useCallback(async (emitEvent = true) => {
    // Stop all media
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
    }

    // Destroy peer
    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }

    // Notify other party and disconnect socket
    if (emitEvent && socketRef.current) {
      socketRef.current.emit('end-call');
    }
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Mark consultation as completed
    try {
      await api.post(`/video/consultation/${consultationId}/end`);
    } catch (err) {
      // Silently ignore — may already be completed
    }

    // Navigate back
    if (user?.role === 'lawyer') {
      navigate('/lawyer/dashboard');
    } else {
      navigate('/consultations');
    }
  }, [consultationId, navigate, user]);

  // Initialize media and socket connection
  useEffect(() => {
    if (!consultation || error) return;
    // Prevent double-start from React StrictMode
    if (callStartedRef.current) return;
    callStartedRef.current = true;

    let socket = null;
    let stream = null;
    let cancelled = false;

    const init = async () => {
      try {
        // Get local media stream
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        // Mark consultation as in_progress
        try {
          await api.post(`/video/consultation/${consultationId}/start`);
        } catch (e) {
          // Ignore — may already be in_progress
        }

        if (cancelled) return;

        // Connect to signaling server
        const token = localStorage.getItem('token');
        socket = io(API_URL, {
          auth: { token },
          transports: ['websocket', 'polling'],
        });
        socketRef.current = socket;

        socket.on('connect', () => {
          if (cancelled) return;
          setConnected(true);
          socket.emit('join-room', { consultationId });
        });

        socket.on('connect_error', (err) => {
          console.error('Socket connection error:', err.message);
          if (!cancelled) {
            setError('Failed to connect to signaling server');
          }
        });

        // When other user is already in the room — we initiate the peer connection
        socket.on('room-users', ({ users }) => {
          if (cancelled) return;
          if (users.length > 0) {
            const remoteUser = users[0];
            setRemoteName(remoteUser.userName);
            setRemoteRole(remoteUser.userRole);
            createPeer(remoteUser.socketId, stream, true);
          }
        });

        // When another user joins — they will initiate, we respond
        socket.on('user-joined', ({ socketId, userName, userRole }) => {
          if (cancelled) return;
          setRemoteName(userName);
          setRemoteRole(userRole);
          createPeer(socketId, stream, false);
        });

        // Relay WebRTC signals — buffer if peer not ready yet
        socket.on('signal', ({ from, signal }) => {
          if (cancelled) return;
          if (peerRef.current && !peerRef.current.destroyed) {
            try {
              peerRef.current.signal(signal);
            } catch (e) {
              console.error('Error signaling peer:', e);
            }
          } else {
            // Buffer signal until peer is created
            pendingSignalsRef.current.push(signal);
          }
        });

        // User left
        socket.on('user-left', () => {
          if (cancelled) return;
          setPeerConnected(false);
          setRemoteName('');
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
          }
          if (peerRef.current) {
            peerRef.current.destroy();
            peerRef.current = null;
          }
        });

        // Call ended by other party
        socket.on('call-ended', () => {
          if (!cancelled) handleEndCall(false);
        });

        socket.on('error', ({ message }) => {
          if (!cancelled) setError(message);
        });
      } catch (err) {
        if (cancelled) return;
        if (err.name === 'NotAllowedError') {
          setError('Camera/microphone access denied. Please allow access and try again.');
        } else {
          setError('Failed to start video call: ' + err.message);
        }
        console.error('Start call error:', err);
      }
    };

    init();

    // Cleanup on unmount (or StrictMode re-mount)
    return () => {
      cancelled = true;
      callStartedRef.current = false;

      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      if (peerRef.current) {
        peerRef.current.destroy();
        peerRef.current = null;
      }
      if (socket) {
        socket.disconnect();
        socketRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      pendingSignalsRef.current = [];
      localStreamRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultation, consultationId, createPeer, handleEndCall]);

  // Toggle audio
  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };

  // Toggle screen sharing
  const toggleScreenShare = async () => {
    if (screenSharing) {
      // Stop screen sharing, restore camera
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((t) => t.stop());
        screenStreamRef.current = null;
      }
      const videoTrack = localStreamRef.current?.getVideoTracks()[0];
      if (peerRef.current && videoTrack) {
        const senders = peerRef.current._pc?.getSenders();
        const videoSender = senders?.find((s) => s.track?.kind === 'video');
        if (videoSender) {
          videoSender.replaceTrack(videoTrack);
        }
      }
      if (localVideoRef.current && localStreamRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
      setScreenSharing(false);
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
        });
        screenStreamRef.current = screenStream;

        const screenTrack = screenStream.getVideoTracks()[0];
        if (peerRef.current) {
          const senders = peerRef.current._pc?.getSenders();
          const videoSender = senders?.find((s) => s.track?.kind === 'video');
          if (videoSender) {
            videoSender.replaceTrack(screenTrack);
          }
        }
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }

        screenTrack.onended = () => {
          toggleScreenShare();
        };

        setScreenSharing(true);
      } catch (err) {
        console.error('Screen share error:', err);
      }
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Determine the other party's name
  const otherPartyName = consultation
    ? user?.role === 'lawyer'
      ? consultation.client?.name
      : consultation.lawyer?.name
    : '';

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: '#1A1A1A',
        }}
      >
        <CircularProgress sx={{ color: axelionColors.gold }} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: '#1A1A1A',
          color: 'white',
          gap: 3,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" sx={{ color: axelionColors.error }}>
          Connection Error
        </Typography>
        <Typography variant="body1" sx={{ color: axelionColors.textMuted, maxWidth: 400 }}>
          {error}
        </Typography>
        <Box
          component="button"
          onClick={() => navigate(-1)}
          sx={{
            mt: 2,
            bgcolor: axelionColors.gold,
            color: 'white',
            px: 4,
            py: 1.5,
            borderRadius: '12px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: 500,
            '&:hover': { bgcolor: axelionColors.goldDark },
          }}
        >
          Go Back
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: '#0D0D0D',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
        animation: `${fadeIn} 0.3s ease-out`,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          bgcolor: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            sx={{
              color: axelionColors.gold,
              fontWeight: 300,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              fontSize: '0.8rem',
            }}
          >
            MASLAXAT
          </Typography>
          <Box sx={{ width: 1, height: 20, bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
            Video Consultation
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {peerConnected && (
            <Chip
              label={formatDuration(callDuration)}
              size="small"
              sx={{
                bgcolor: 'rgba(122,154,107,0.2)',
                color: axelionColors.success,
                border: `1px solid ${axelionColors.success}`,
                fontFamily: 'monospace',
                letterSpacing: '0.1em',
              }}
            />
          )}
          <Chip
            label={peerConnected ? 'Connected' : connected ? 'Waiting...' : 'Connecting...'}
            size="small"
            sx={{
              bgcolor: peerConnected
                ? 'rgba(122,154,107,0.15)'
                : 'rgba(196,163,90,0.15)',
              color: peerConnected ? axelionColors.success : axelionColors.warning,
              border: `1px solid ${peerConnected ? axelionColors.success : axelionColors.warning}`,
              animation: !peerConnected ? `${pulse} 2s ease-in-out infinite` : 'none',
            }}
          />
        </Box>
      </Box>

      {/* Main video area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Remote video (full screen) */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#0D0D0D',
          }}
        >
          {peerConnected ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                animation: `${fadeIn} 0.5s ease-out`,
              }}
            >
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 3,
                  bgcolor: axelionColors.bgDarkElevated,
                  border: `2px solid ${axelionColors.gold}`,
                  fontSize: '2.5rem',
                  color: axelionColors.gold,
                }}
              >
                {otherPartyName?.charAt(0) || '?'}
              </Avatar>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '1.3rem',
                  fontWeight: 300,
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                {otherPartyName || 'Participant'}
              </Typography>
              <Typography
                sx={{
                  color: axelionColors.textMuted,
                  fontSize: '0.85rem',
                  mb: 3,
                }}
              >
                {connected
                  ? 'Waiting for the other party to join...'
                  : 'Connecting to the room...'}
              </Typography>
              <CircularProgress
                size={32}
                thickness={2}
                sx={{ color: axelionColors.gold }}
              />
            </Box>
          )}
        </Box>

        {/* Local video (picture-in-picture) */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 100,
            right: 24,
            width: { xs: 140, sm: 200, md: 260 },
            aspectRatio: '4/3',
            borderRadius: '16px',
            overflow: 'hidden',
            border: `2px solid ${axelionColors.gold}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            bgcolor: '#1A1A1A',
            zIndex: 5,
          }}
        >
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)',
            }}
          />
          {!videoEnabled && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#1A1A1A',
              }}
            >
              <VideocamOff sx={{ color: axelionColors.textMuted, fontSize: 40 }} />
            </Box>
          )}
          <Typography
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 12,
              color: 'white',
              fontSize: '0.75rem',
              textShadow: '0 1px 4px rgba(0,0,0,0.8)',
            }}
          >
            You
          </Typography>
        </Box>

        {/* Remote user info overlay */}
        {peerConnected && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1,
              borderRadius: '12px',
              bgcolor: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(10px)',
              zIndex: 5,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: axelionColors.gold,
                fontSize: '0.8rem',
              }}
            >
              {remoteName?.charAt(0)}
            </Avatar>
            <Box>
              <Typography sx={{ color: 'white', fontSize: '0.85rem', fontWeight: 500 }}>
                {remoteName}
              </Typography>
              <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.7rem' }}>
                {remoteRole === 'lawyer' ? 'Lawyer' : 'Client'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Controls bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2.5 },
          py: 2.5,
          px: 3,
          bgcolor: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Mic toggle */}
        <IconButton
          onClick={toggleAudio}
          sx={{
            width: 56,
            height: 56,
            bgcolor: audioEnabled
              ? 'rgba(255,255,255,0.1)'
              : axelionColors.error,
            color: 'white',
            border: `1px solid ${audioEnabled ? 'rgba(255,255,255,0.15)' : 'transparent'}`,
            '&:hover': {
              bgcolor: audioEnabled
                ? 'rgba(255,255,255,0.2)'
                : '#9A5555',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {audioEnabled ? <Mic /> : <MicOff />}
        </IconButton>

        {/* Camera toggle */}
        <IconButton
          onClick={toggleVideo}
          sx={{
            width: 56,
            height: 56,
            bgcolor: videoEnabled
              ? 'rgba(255,255,255,0.1)'
              : axelionColors.error,
            color: 'white',
            border: `1px solid ${videoEnabled ? 'rgba(255,255,255,0.15)' : 'transparent'}`,
            '&:hover': {
              bgcolor: videoEnabled
                ? 'rgba(255,255,255,0.2)'
                : '#9A5555',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {videoEnabled ? <Videocam /> : <VideocamOff />}
        </IconButton>

        {/* Screen share */}
        <IconButton
          onClick={toggleScreenShare}
          sx={{
            width: 56,
            height: 56,
            bgcolor: screenSharing
              ? axelionColors.gold
              : 'rgba(255,255,255,0.1)',
            color: 'white',
            border: `1px solid ${screenSharing ? 'transparent' : 'rgba(255,255,255,0.15)'}`,
            '&:hover': {
              bgcolor: screenSharing
                ? axelionColors.goldDark
                : 'rgba(255,255,255,0.2)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {screenSharing ? <StopScreenShare /> : <ScreenShare />}
        </IconButton>

        {/* Fullscreen */}
        <IconButton
          onClick={toggleFullscreen}
          sx={{
            width: 56,
            height: 56,
            bgcolor: 'rgba(255,255,255,0.1)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.15)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            transition: 'all 0.2s ease',
          }}
        >
          {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>

        {/* End call */}
        <IconButton
          onClick={() => handleEndCall(true)}
          sx={{
            width: 72,
            height: 56,
            borderRadius: '28px',
            bgcolor: '#D32F2F',
            color: 'white',
            '&:hover': { bgcolor: '#B71C1C' },
            transition: 'all 0.2s ease',
            ml: 1,
          }}
        >
          <CallEnd />
        </IconButton>
      </Box>
    </Box>
  );
};

export default VideoCallPage;
